import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactNode, HTMLAttributes } from 'react'
import { useState, forwardRef } from 'react'
import ContextMenu from './ContextMenu'
import { Tooltip, TooltipTrigger, TooltipContent } from './interactives/Tooltip'
import { cn } from '@/utils'

/**
 * Note for the future:
 * This button component does way too much! Better have two components, one for the button and another for the
 * dropdown menu. Also the tooltip functionality should be handeled outside the Button.
 * Single responsibility principle: A component should do one thing.
 */

export interface ButtonProps extends HTMLAttributes<HTMLDivElement> {
  text?: ReactNode
  title?: string
  disabled?: boolean
  loading?: boolean
  css?: string // DEPRECATED, use buttonClass and wrapperClass instead
  className?: string // DEPRECATED, use buttonClass and wrapperClass instead
  wrapperClass?: string
  buttonClass?: string
  icon?: IconDefinition | null
  onClick?: () => void
  colour?: 'main' | 'dark' | 'red' | 'none'
  size?: 'sm' | 'md'
  toolTip?: ReactNode
  type?: 'button' | 'submit' | 'reset'
  secondaryMenu?: boolean
  secondaryMenuItems?: Array<object> // Could be typed more specifically
  secondaryMenuOnClick?: (choice: string) => void
  iconAfter?: IconDefinition | null
  showTooltipWhenDisabled?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      text = '',
      title = undefined,
      disabled = false,
      loading = false,
      css = '',
      className = '',
      wrapperClass = '',
      buttonClass = '',
      icon = null,
      onClick = () => {},
      colour = 'main',
      size = 'md',
      toolTip = '',
      type = 'button',
      secondaryMenu = false,
      secondaryMenuItems = [],
      secondaryMenuOnClick = () => {},
      iconAfter = null,
      showTooltipWhenDisabled = false,
      ...props
    },
    ref,
  ) {
    const [contextOpen, setContextOpen] = useState<MouseEvent | null>(null)

    const iconOnly = !!icon && !text
    const h: Record<string, string> = { sm: 'h-7', md: 'h-9' }
    const w: Record<string, string> = { sm: 'w-7', md: 'w-9' }
    const px: Record<string, string> = { sm: 'px-1', md: 'px-3' }
    const font: Record<string, string> = { sm: 'text-sm', md: 'text-md' }

    const ButtonComponent = (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        onClick={onClick}
        title={title}
        className={cn(
          'relative inline-flex items-center justify-center hover:brightness-125 transition-[filter] font-semibold focus:outline-hidden focus:ring-3 focus:ring-offset-1 focus:ring-offset-transparent whitespace-nowrap',
          'disabled:brightness-100 disabled:opacity-50 disabled:pointer-events-none',
          colour === 'main' && 'bg-blue-500 text-white',
          colour === 'red' && 'bg-red-500 text-white',
          colour === 'dark' && 'bg-gray-900 text-gray-200',
          h[size],
          iconOnly ? w[size] : px[size],
          text && font[size],
          loading &&
            colour === 'main' &&
            'bg-animated from-blue-700 to-blue-600',
          loading && colour === 'red' && 'bg-animated from-red-700 to-red-600',
          loading &&
            colour === 'dark' &&
            'bg-animated from-gray-800 to-gray-600',
          secondaryMenu ? 'rounded-l' : 'rounded-sm',
          icon && 'pl-0!',
          iconAfter && 'pr-0!',
          buttonClass,
          className || css,
        )}
      >
        {icon && (
          <span
            className={cn(
              'text-center',
              size === 'sm' && 'w-7',
              size === 'md' && 'w-9',
            )}
          >
            <FontAwesomeIcon icon={icon} size="sm" fixedWidth />
          </span>
        )}
        <span className="text-ellipsis overflow-hidden">{text}</span>
        {iconAfter && (
          <span
            className={cn(
              'ml-1 text-center',
              size === 'sm' && 'w-7',
              size === 'md' && 'w-9',
            )}
          >
            <FontAwesomeIcon icon={iconAfter} fixedWidth />
          </span>
        )}
      </button>
    )

    return (
      <div className={['flex', wrapperClass].join(' ')} {...props}>
        {toolTip ? (
          <Tooltip disabled={disabled && !showTooltipWhenDisabled}>
            <TooltipTrigger className="w-full">
              {ButtonComponent}
            </TooltipTrigger>
            <TooltipContent>{toolTip}</TooltipContent>
          </Tooltip>
        ) : (
          ButtonComponent
        )}
        {secondaryMenu && (
          <>
            <button
              onClick={(e) =>
                setContextOpen(e.nativeEvent as unknown as MouseEvent)
              }
              disabled={disabled}
              className={cn(
                'w-full bg-gray-900 px-1 rounded-r border-l-2 border-gray-800 hover:brightness-125 focus:outline-hidden focus:ring-3 focus:ring-offset-1 focus:ring-offset-transparent focus:z-10 cursor-pointer disabled:pointer-events-none disabled:opacity-50',
                h[size],
                loading && colour === 'main'
                  ? 'bg-animated from-blue-700 to-blue-600'
                  : '',
                loading && colour === 'dark'
                  ? 'bg-animated from-gray-800 to-gray-600'
                  : '',
              )}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
            {contextOpen && (
              <ContextMenu
                openEvent={contextOpen}
                items={secondaryMenuItems}
                onClick={(choice: string) => secondaryMenuOnClick(choice)}
                onClose={() => setContextOpen(null)}
              />
            )}
          </>
        )}
      </div>
    )
  },
)
